<template>
    <ElDialog
        ref="dialogRef"
        :visible="modalVisible"
        :before-close="handleBeforeClose"
        :modal="modal"
        :lock-scroll="lockScroll"
        :close-on-click-modal="closeOnClickModal"
        :close-on-press-escape="closeOnPressEscape"
        :show-close="showClose"
        :destroy-on-close="destroyOnClose"
        :append-to-body="appendToBody"
        :modal-append-to-body="true"
        :custom-class="`${customClass} base-modal`"
        :width="width"
        :top="top"
        :center="center"
        data-testid="base-modal"
        @open="onOpen"
        @close="onClose"
        @closed="onClosed"
        @opened="onOpened"
    >
        <template #title>
            <div class="base-modal__title" data-testid="base-modal-title">
                <slot name="title">
                    {{ title }}
                </slot>
            </div>
        </template>
        <div class="base-modal__body" data-testid="base-modal-body">
            <slot />
        </div>
        <template #footer>
            <div
                v-if="$slots.footer"
                class="base-modal__footer"
                data-testid="base-modal-footer"
            >
                <slot name="footer" />
            </div>
        </template>
    </ElDialog>
</template>

<script setup lang="ts">
import { Dialog as ElDialog } from 'element-ui'
import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { TranslateResult } from 'vue-i18n'

interface IProps {
    visible?: boolean
    title?: string | TranslateResult
    modal?: boolean
    lockScroll?: boolean
    closeOnClickModal?: boolean
    closeOnPressEscape?: boolean
    showClose?: boolean
    center?: boolean
    destroyOnClose?: boolean
    appendToBody?: boolean
    top?: string
    width?: string
    customClass?: string
    wrapperClasses?: string
    beforeClose?: (done: () => void) => void
}

const props = withDefaults(
    defineProps<IProps>(),
    {
        title: '',
        top: '15vh',
        width: '50%',
        modal: true,
        center: true,
        appendToBody: true,
        lockScroll: true,
        closeOnClickModal: true,
        closeOnPressEscape: true,
        showClose: true,
        visible: true,
        customClass: '',
        wrapperClasses: '',
        beforeClose: undefined,
    }
)
const emit = defineEmits([
    'update:visible',
    'input',
    'close',
    'closed',
    'open',
    'opened'
])

const dialogRef = ref<InstanceType<typeof ElDialog> | null>(null)
const mouseDownInsideDialog = ref(false)

const modalVisible = computed({
    get () {
        return props.visible
    },
    set (val) {
        emit('input', val)
        emit('update:visible', val)
    }
})

function isClickInsideDialog (target: EventTarget | null): boolean {
    if (!target || !dialogRef.value) return false

    const headerBtn = target instanceof Element ? target.closest('.el-dialog__headerbtn') : null
    if (headerBtn && headerBtn.closest('.el-dialog__header') && headerBtn.querySelector('.el-dialog__close')) {
        return false
    }

    let dialogElement: HTMLElement | null = null

    if (dialogRef.value.$el instanceof HTMLElement) {
        dialogElement = dialogRef.value.$el.querySelector('.el-dialog')
    }

    if (!dialogElement) return false
    
    return dialogElement.contains(target as Node)
}

function handleMouseDown (event: MouseEvent) {
    if (!props.visible) {
        mouseDownInsideDialog.value = false
        return
    }
    
    mouseDownInsideDialog.value = isClickInsideDialog(event.target)
}

function handleBeforeClose (done: () => void) {
    if (mouseDownInsideDialog.value) {
        mouseDownInsideDialog.value = false
        return
    }
    
    mouseDownInsideDialog.value = false
    
    if (props.beforeClose) {
        props.beforeClose(done)
    } else {
        done()
    }
}

function onOpen () {
    emit('open')
}

function onOpened () {
    emit('opened')
}

function onClose () {
    mouseDownInsideDialog.value = false
    if (!props.visible) return
    modalVisible.value = false
    emit('close')
}

function onClosed () {
    emit('closed')
}

onMounted(() => {
    document.addEventListener('mousedown', handleMouseDown)
})

onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleMouseDown)
})
</script>

<style lang="scss">
.base-modal.el-dialog {
    @apply rounded-lg;
    color: var(--mainTextColor-color);

    .el-dialog__header {
        @apply rounded-t-lg text-lg font-semibold min-h-14 border-b border-ui-lines;
        background-color: var(--mainBgColor-color);

        .el-dialog__headerbtn:hover, .el-dialog__headerbtn:focus {
            .el-icon-close {
                @apply text-primary-actions;
            }
        }
    }

    .el-dialog__body {
        @apply rounded-b-lg;
        color: var(--mainTextColor-color);
        background-color: var(--mainBgColor-color) !important;
    }

    .base-modal__body {
        @apply py-6;
    }

    .el-dialog__footer {
        @apply rounded-b-lg px-8 border-t border-ui-lines py-4;
        background-color: var(--mainBgColor-color);

        .base-modal__footer {
            @apply flex items-center justify-end gap-2 xl:gap-3 w-full;
        }
    }

    &:has(.el-dialog__footer) {
        .el-dialog__body {
            @apply border-b-0 rounded-none;
        }
    }
}
</style>
